import 'react-datepicker/dist/react-datepicker.css';
import '../PunchupDateTimePicker/PunchupDateTimePicker.css';
import './PunchupDateRange.css';

import DatePicker from 'react-datepicker';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import styles from './PunchupDateRange.module.css';
import { PunchupDateRangeInput } from './PunchupDateRangeInput';

export interface PunchupDateRangeProps {
	/** Currently selected start of the range. */
	startDate: Date | null;
	/** Currently selected end of the range. */
	endDate: Date | null;
	/** Earliest selectable date. */
	minDate?: Date;
	/** Latest selectable date. */
	maxDate?: Date;
	/** Called when the user completes a range selection (both dates chosen). */
	onChange: (start: Date, end: Date) => void;
	/** When true, renders only the calendar icon — no date text. Use in chart
	 * headers where horizontal space is limited. The popup opens identically. */
	iconOnly?: boolean;
}

/* Approximate rendered popup width (two calendars side by side); used to decide
 * whether the popup must right-flip to stay inside the viewport. Keep in sync
 * with the popup layout in PunchupDateRange.css. */
const POPUP_WIDTH = 680;

const formatDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const isSameDay = (a: Date, b: Date): boolean =>
	a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const clampDate = (date: Date, min?: Date, max?: Date): Date => {
	if (min && date < min) return min;
	if (max && date > max) return max;
	return date;
};

/**
 * Determine the two months to display when the popup first opens.
 *
 * Rules:
 *   - start and end in different months → show start-month left, end-month right
 *   - start and end in the same month, or no end → show preceding month left, start-month right
 *   - no start → show preceding month left, current month right
 */
function computeInitialMonths(start: Date | null, end: Date | null): [Date, Date] {
	const monthOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
	const prior = (d: Date) => new Date(d.getFullYear(), d.getMonth() - 1, 1);

	if (!start) {
		const right = monthOf(new Date());
		return [prior(right), right];
	}
	const sm = monthOf(start);
	if (!end) return [prior(sm), sm];
	const em = monthOf(end);
	return sm.getTime() === em.getTime() ? [prior(sm), sm] : [sm, em];
}

/* ─── CalendarHeader — module level (stable identity) ───────────────────── */

interface HeaderProps {
	date: Date;
	onPrev: () => void;
	onNext: () => void;
	prevDisabled: boolean;
	nextDisabled: boolean;
}

const CalendarHeader: FC<HeaderProps> = ({ date, onPrev, onNext, prevDisabled, nextDisabled }) => (
	<div className="punchupDateRangeCalHeader">
		<button
			type="button"
			className="punchupDateRangeNavBtn"
			onClick={onPrev}
			disabled={prevDisabled}
			aria-label="Previous month"
		>
			<ChevronLeft size={14} />
		</button>
		<span className="punchupDateRangeMonthLabel">
			{date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
		</span>
		<button
			type="button"
			className="punchupDateRangeNavBtn"
			onClick={onNext}
			disabled={nextDisabled}
			aria-label="Next month"
		>
			<ChevronRight size={14} />
		</button>
	</div>
);
CalendarHeader.displayName = 'CalendarHeader';

/* ─── Main component ─────────────────────────────────────────────────────── */

export const PunchupDateRange: FC<PunchupDateRangeProps> = ({
	startDate,
	endDate,
	minDate,
	maxDate,
	onChange,
	iconOnly,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [localStart, setLocalStart] = useState<Date | null>(startDate);
	const [localEnd, setLocalEnd] = useState<Date | null>(endDate);
	const [hoverDate, setHoverDate] = useState<Date | null>(null);
	/* initMonths drives openToDate on both DatePicker instances. Set in handleToggle
	 * so every popup open starts at the right months for the current selection. */
	const [initMonths, setInitMonths] = useState<[Date, Date]>(() => computeInitialMonths(startDate, endDate));
	const [popupFlip, setPopupFlip] = useState(false);

	const containerRef = useRef<HTMLDivElement>(null);

	/* Sync when the parent resets the range (e.g. analytics granularity change).
	 * Timestamp guard prevents spurious updates when the parent passes a new Date
	 * reference with the same value — breaking a potential render loop.
	 * Calendar positions are NOT reset here — they are computed at popup-open time. */
	const prevStartRef = useRef<Date | null>(startDate);
	const prevEndRef = useRef<Date | null>(endDate);
	useEffect(() => {
		const sameStart = prevStartRef.current?.getTime() === startDate?.getTime();
		const sameEnd = prevEndRef.current?.getTime() === endDate?.getTime();
		prevStartRef.current = startDate;
		prevEndRef.current = endDate;
		if (sameStart && sameEnd) return;
		setLocalStart(startDate);
		setLocalEnd(endDate);
	}, [startDate, endDate]);

	/* Close popup when the user clicks outside the component. */
	useEffect(() => {
		if (!isOpen) return;
		const handleMouseDown = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleMouseDown);
		return () => document.removeEventListener('mousedown', handleMouseDown);
	}, [isOpen]);

	/* Close popup on Escape key. */
	useEffect(() => {
		if (!isOpen) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setIsOpen(false);
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isOpen]);

	/* Open/close toggle. On open: compute calendar starting months from the current
	 * local range, and pre-compute whether the popup needs to flip right-anchored
	 * to avoid overflowing the viewport (popup width ≈ 680px). */
	const handleToggle = useCallback(() => {
		if (isOpen) {
			setIsOpen(false);
			return;
		}
		setInitMonths(computeInitialMonths(localStart, localEnd));
		const rect = containerRef.current?.getBoundingClientRect();
		setPopupFlip(rect ? rect.right + POPUP_WIDTH > window.innerWidth : false);
		setIsOpen(true);
	}, [isOpen, localStart, localEnd]);

	/* Two-click range selection:
	 *   click 1 → set start, clear end + hover
	 *   click 2 ≥ start → set end, call onChange, clear hover, close
	 *   click 2 < start → restart (treat as new start) */
	const handleDayClick = useCallback(
		(date: Date | null) => {
			if (!date) return;
			if (!localStart || localEnd) {
				setLocalStart(date);
				setLocalEnd(null);
				setHoverDate(null);
			} else if (date >= localStart) {
				setLocalEnd(date);
				setHoverDate(null);
				onChange(localStart, date);
				setIsOpen(false);
			} else {
				setLocalStart(date);
				setLocalEnd(null);
				setHoverDate(null);
			}
		},
		[localStart, localEnd, onChange]
	);

	/* Returns class names for range highlighting.
	 * We reuse the react-datepicker class names so that PunchupDateTimePicker.css
	 * sections 12–13 apply without any additional CSS. */
	const getDayClassName = useCallback(
		(date: Date): string => {
			if (!localStart) return '';

			const effectiveEnd = localEnd ?? (hoverDate && hoverDate >= localStart ? hoverDate : null);
			const isStart = isSameDay(date, localStart);
			const isEnd = effectiveEnd ? isSameDay(date, effectiveEnd) : false;

			if (isStart && isEnd) {
				return 'react-datepicker__day--range-start react-datepicker__day--range-end';
			}

			const classes: string[] = [];
			if (isStart) {
				classes.push(localEnd ? 'react-datepicker__day--range-start' : 'react-datepicker__day--selecting-range-start');
			}
			if (isEnd) {
				classes.push(localEnd ? 'react-datepicker__day--range-end' : 'react-datepicker__day--selecting-range-end');
			}
			if (effectiveEnd && date > localStart && date < effectiveEnd) {
				classes.push(localEnd ? 'react-datepicker__day--in-range' : 'react-datepicker__day--in-selecting-range');
			}
			return classes.join(' ');
		},
		[localStart, localEnd, hoverDate]
	);

	/* renderDayContents attaches a hover listener to each day cell so the live
	 * selection preview updates as the mouse moves between first and second click. */
	const renderDayContents = useCallback(
		(day: number, date: Date) => (
			<span onMouseEnter={() => setHoverDate((prev) => (prev?.getTime() === date.getTime() ? prev : date))}>{day}</span>
		),
		[]
	);

	/* Shared renderCustomHeader for both calendars. CalendarHeader is stable
	 * (module-level); decreaseMonth / increaseMonth come from react-datepicker's
	 * render-prop and are bound to each calendar's internal state. */
	const renderCalendarHeader = useCallback(
		({
			date,
			decreaseMonth,
			increaseMonth,
			prevMonthButtonDisabled,
			nextMonthButtonDisabled,
		}: {
			date: Date;
			decreaseMonth: () => void;
			increaseMonth: () => void;
			prevMonthButtonDisabled: boolean;
			nextMonthButtonDisabled: boolean;
		}) => (
			<CalendarHeader
				date={date}
				onPrev={decreaseMonth}
				onNext={increaseMonth}
				prevDisabled={prevMonthButtonDisabled}
				nextDisabled={nextMonthButtonDisabled}
			/>
		),
		[]
	);

	/* Manual date-input handlers.
	 * Start input: clamp to [minDate, maxDate]; if new start > current end, clear end.
	 * End input:   clamp to [minDate, maxDate]; if new end < current start, swap them.
	 *              If there is no start yet, set end silently without calling onChange
	 *              (a partial range is not a completed range). */
	const handleStartInputChange = useCallback(
		(date: Date) => {
			const clamped = clampDate(date, minDate, maxDate);
			const newEnd = localEnd && clamped > localEnd ? null : localEnd;
			setLocalStart(clamped);
			setLocalEnd(newEnd);
			if (newEnd) onChange(clamped, newEnd);
		},
		[localEnd, minDate, maxDate, onChange]
	);

	const handleEndInputChange = useCallback(
		(date: Date) => {
			const clamped = clampDate(date, minDate, maxDate);
			if (!localStart) {
				setLocalEnd(clamped);
				return;
			}
			const newStart = clamped < localStart ? clamped : localStart;
			setLocalStart(newStart);
			setLocalEnd(clamped);
			onChange(newStart, clamped);
		},
		[localStart, minDate, maxDate, onChange]
	);

	return (
		<div className={`${styles.container} punchupDateRangeControl`} ref={containerRef}>
			{/* Trigger button — shows the current range (or just the calendar icon) */}
			<button
				type="button"
				className={`punchupDateRangeTrigger${iconOnly ? ' punchupDateRangeTriggerIconOnly' : ''}`}
				onClick={handleToggle}
				aria-expanded={isOpen}
				aria-label={iconOnly ? 'Select date range' : undefined}
			>
				<CalendarDays size={13} className="punchupDateRangeTriggerIcon" />
				{!iconOnly && (
					<span className={styles.triggerText}>
						{localStart ? formatDate(localStart) : '–'}
						{' – '}
						{localEnd ? formatDate(localEnd) : '–'}
					</span>
				)}
			</button>

			{/* Popup — two independent month calendars; mounts fresh on every open */}
			{isOpen && (
				<div
					className={`punchupDateRangePopup${popupFlip ? ' punchupDateRangePopupFlip' : ''}`}
					onMouseLeave={() => {
						if (!localEnd) setHoverDate(null);
					}}
				>
					{/* Manual date inputs — centered above the calendars */}
					<div className="punchupDateRangeInputRow">
						<PunchupDateRangeInput value={localStart ?? undefined} onChange={handleStartInputChange} />
						<span className="punchupDateRangeInputSep">–</span>
						<PunchupDateRangeInput value={localEnd ?? undefined} onChange={handleEndInputChange} />
					</div>

					{/* Two inline calendars side by side with independent navigation */}
					<div className="punchupDateRangeCalendars">
						<DatePicker
							inline
							openToDate={initMonths[0]}
							selected={null}
							onChange={handleDayClick}
							dayClassName={getDayClassName}
							renderDayContents={renderDayContents}
							renderCustomHeader={renderCalendarHeader}
							minDate={minDate}
							maxDate={maxDate}
						/>
						<DatePicker
							inline
							openToDate={initMonths[1]}
							selected={null}
							onChange={handleDayClick}
							dayClassName={getDayClassName}
							renderDayContents={renderDayContents}
							renderCustomHeader={renderCalendarHeader}
							minDate={minDate}
							maxDate={maxDate}
						/>
					</div>
				</div>
			)}
		</div>
	);
};
